import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import useAsync from 'react-use/lib/useAsync';
import type { Entity } from '@backstage/catalog-model';
import { useApi, useRouteRefParams } from '@backstage/core-plugin-api';
import { catalogApiRef, entityRouteRef } from '@backstage/plugin-catalog-react';
import { useMixpanelAnalytics, getLastInAppPath } from '../../lib/analytics';

/**
 * The entity of the current entity-page URL. The tracker is mounted next to the
 * alpha catalog's entity page, outside its entity provider, so it loads the
 * entity itself (once per entity, not per tab). A previous entity is not
 * returned while the next one loads.
 */
function useEntityFromRoute(): Entity | undefined {
  const { kind, namespace, name } = useRouteRefParams(entityRouteRef);
  const catalogApi = useApi(catalogApiRef);
  const key = `${kind}:${namespace}/${name}`;
  const { value } = useAsync(
    async () => ({
      key,
      entity: await catalogApi.getEntityByRef({ kind, namespace, name }),
    }),
    [catalogApi, key],
  );
  return value?.key === key ? value.entity : undefined;
}

/**
 * Sends `catalog_entity_viewed` once per entity and `catalog_entity_tab_changed`
 * on every tab switch. Mounted once per entity page, across all tabs and on
 * direct loads of any tab, by the `page:catalog/entity` override in
 * `modules/catalog/entityPage.tsx` (PAT-1027; it used to sit at the root of
 * `entityPage` in `EntityPage.tsx`, which compat no longer renders).
 */
export function EntityAnalyticsTracker() {
  const entity = useEntityFromRoute();
  const location = useLocation();
  const { track } = useMixpanelAnalytics();
  const prevTabRef = useRef<string>('');
  const prevEntityRefRef = useRef<string>('');

  useEffect(() => {
    if (!entity) return;
    const kind = entity.kind ?? '';
    const namespace = entity.metadata.namespace ?? 'default';
    const name = entity.metadata.name ?? '';
    const entityRef = `${kind.toLowerCase()}:${namespace}/${name}`;
    const spec = (entity.spec ?? {}) as Record<string, any>;

    // Derive source from the previous in-app pathname (tracked by
    // MixpanelAnalyticsApi). document.referrer reflects only the external
    // referrer in an SPA, so it would misclassify catalog-list navigations.
    const previousPath = getLastInAppPath();
    const isCatalogList =
      previousPath.startsWith('/catalog') &&
      previousPath.split('/').length <= 3;
    const source = isCatalogList ? 'catalog_list' : 'direct_link';

    // Extract current tab from location pathname (sub-path after entity path).
    const pathParts = location.pathname.split('/');
    const tabViewed = (pathParts.length > 5 ? pathParts[5] : '') || 'overview';

    track('catalog_entity_viewed', {
      entity_ref: entityRef,
      entity_kind: kind,
      entity_type: spec.type ?? '',
      entity_owner: spec.owner ?? '',
      entity_lifecycle: spec.lifecycle ?? '',
      tab_viewed: tabViewed,
      source,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity?.metadata.name, entity?.metadata.namespace, entity?.kind]);

  // Track tab changes
  useEffect(() => {
    if (!entity) return;
    const kind = entity.kind ?? '';
    const namespace = entity.metadata.namespace ?? 'default';
    const name = entity.metadata.name ?? '';
    const entityRef = `${kind.toLowerCase()}:${namespace}/${name}`;

    // Extract tab from location pathname (the sub-path after the entity path)
    const pathParts = location.pathname.split('/');
    const tabName = pathParts.length > 5 ? pathParts[5] : 'overview';
    const currentTab = tabName || 'overview';

    // Reset tab tracking when the entity changes — otherwise the new
    // entity's initial tab would be reported as a "change" from the
    // previous entity's last tab.
    if (prevEntityRefRef.current !== entityRef) {
      prevEntityRefRef.current = entityRef;
      prevTabRef.current = currentTab;
      return;
    }

    if (prevTabRef.current && prevTabRef.current !== currentTab) {
      track('catalog_entity_tab_changed', {
        entity_ref: entityRef,
        tab_name: currentTab,
        previous_tab: prevTabRef.current,
      });
    }
    prevTabRef.current = currentTab;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    location.pathname,
    entity?.metadata.name,
    entity?.metadata.namespace,
    entity?.kind,
  ]);

  return null;
}
